import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TooltipDirective } from '../../../../shared/components/ui/tooltip/tooltip.directive';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faXmark,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';

import { Button } from '../../../../shared/components/ui/buttons/gradient-btn/button';
import { cropToAvatarDataUrl, loadImage } from '../../../../shared/helpers/avatar-image';

export interface AvatarCropperData {
  file: File;
}

// On-screen crop viewport (square), fixed in CSS. The model below positions and
// scales the source image relative to this frame; the same model derives the
// natural-pixel crop rect on apply.
const FRAME_PX = 256;
const MAX_ZOOM = 4;

@Component({
  selector: 'andor-avatar-cropper-dialog',
  standalone: true,
  imports: [CommonModule, TranslatePipe, TooltipDirective, FaDuotoneIconComponent, Button],
  templateUrl: './avatar-cropper-dialog.html',
  styleUrl: './avatar-cropper-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarCropperDialog implements OnDestroy {
  private readonly _dialogRef =
    inject<MatDialogRef<AvatarCropperDialog, string | null>>(MatDialogRef);
  private readonly _data = inject<AvatarCropperData>(MAT_DIALOG_DATA);

  protected readonly faXmark = faXmark;
  protected readonly faPlus = faMagnifyingGlassPlus;
  protected readonly faMinus = faMagnifyingGlassMinus;
  protected readonly framePx = FRAME_PX;
  protected readonly maxZoom = MAX_ZOOM;

  protected readonly imageUrl = signal('');
  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);

  // User zoom multiplier applied on top of the cover-fit base scale (>= 1).
  protected readonly zoom = signal(1);
  // Source image natural size and the cover-fit scale at zoom=1 (so the image
  // exactly fills the frame). Signals so the layout computeds react on load.
  private readonly natural = signal({ w: 0, h: 0 });
  private readonly baseScale = signal(1);
  // Pan offset (px) of the image center relative to the frame center.
  private readonly offset = signal({ x: 0, y: 0 });

  private image?: HTMLImageElement;
  private objectUrl?: string;
  private dragStart: { px: number; py: number; ox: number; oy: number } | null = null;

  // Displayed image size: natural * cover-fit * user zoom.
  protected readonly displaySize = computed(() => {
    const n = this.natural();
    const s = this.baseScale() * this.zoom();
    return { w: n.w * s, h: n.h * s };
  });

  // Absolute position of the image inside the frame, driven entirely by the
  // pan/zoom model (no CSS transform, so the math stays trivial).
  protected readonly imageStyle = computed(() => {
    const { w, h } = this.displaySize();
    const { x, y } = this.offset();
    return {
      width: `${w}px`,
      height: `${h}px`,
      left: `${FRAME_PX / 2 + x - w / 2}px`,
      top: `${FRAME_PX / 2 + y - h / 2}px`,
    };
  });

  constructor() {
    void this._load();
  }

  private async _load(): Promise<void> {
    try {
      const { image, objectUrl } = await loadImage(this._data.file);
      this.image = image;
      this.objectUrl = objectUrl;
      this.natural.set({ w: image.naturalWidth, h: image.naturalHeight });
      this.baseScale.set(Math.max(FRAME_PX / image.naturalWidth, FRAME_PX / image.naturalHeight));
      this.imageUrl.set(objectUrl);
      this.loading.set(false);
    } catch {
      this.error.set('Could not load this image.');
      this.loading.set(false);
    }
  }

  protected onPointerDown(ev: PointerEvent): void {
    if (this.loading()) return;
    (ev.target as HTMLElement).setPointerCapture(ev.pointerId);
    const o = this.offset();
    this.dragStart = { px: ev.clientX, py: ev.clientY, ox: o.x, oy: o.y };
  }

  protected onPointerMove(ev: PointerEvent): void {
    if (!this.dragStart) return;
    this.offset.set(
      this._clampOffset({
        x: this.dragStart.ox + (ev.clientX - this.dragStart.px),
        y: this.dragStart.oy + (ev.clientY - this.dragStart.py),
      })
    );
  }

  protected onPointerUp(): void {
    this.dragStart = null;
  }

  protected onWheel(ev: WheelEvent): void {
    if (this.loading()) return;
    ev.preventDefault();
    this._setZoom(this.zoom() * (ev.deltaY < 0 ? 1.1 : 0.9));
  }

  protected onZoomInput(value: number): void {
    this._setZoom(value);
  }

  private _setZoom(value: number): void {
    this.zoom.set(Math.min(MAX_ZOOM, Math.max(1, value)));
    // Re-clamp the pan so zooming out never exposes empty edges.
    this.offset.set(this._clampOffset(this.offset()));
  }

  // Keep the image covering the frame: the center may move at most half the
  // overflow in each axis.
  private _clampOffset(o: { x: number; y: number }): { x: number; y: number } {
    const { w, h } = this.displaySize();
    const maxX = Math.max(0, (w - FRAME_PX) / 2);
    const maxY = Math.max(0, (h - FRAME_PX) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, o.x)),
      y: Math.min(maxY, Math.max(-maxY, o.y)),
    };
  }

  protected async apply(): Promise<void> {
    if (!this.image || this.saving()) return;
    this.saving.set(true);
    try {
      const { w, h } = this.displaySize();
      const scale = this.baseScale() * this.zoom(); // displayed px per natural px
      const { x, y } = this.offset();
      const imgLeft = FRAME_PX / 2 + x - w / 2;
      const imgTop = FRAME_PX / 2 + y - h / 2;
      const dataUrl = await cropToAvatarDataUrl(this.image, {
        x: -imgLeft / scale,
        y: -imgTop / scale,
        size: FRAME_PX / scale,
      });
      this._dialogRef.close(dataUrl);
    } catch {
      this.error.set('Could not process this image.');
      this.saving.set(false);
    }
  }

  protected cancel(): void {
    this._dialogRef.close(null);
  }

  ngOnDestroy(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
  }
}
